import { DestroyRef, inject, Injectable } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    IAgentClientState,
    IAgentStateTransitionRequested,
    IWorkItemClosedEvent,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { SignalRHubService } from '../signalr.hub.service';

@Injectable({
    providedIn: 'root',
})
export class AgentHubService extends SignalRHubService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #agentStateSubject = new Subject<IAgentClientState>();
    readonly #agentTransitionSubject =
        new Subject<IAgentStateTransitionRequested>();
    readonly #workItemClosedSubject = new Subject<IWorkItemClosedEvent>();
    #heartbeatSub?: Subscription;

    agentState$ = this.#agentStateSubject.asObservable();
    agentTransition$ = this.#agentTransitionSubject.asObservable();
    workItemClosed$ = this.#workItemClosedSubject.asObservable();

    name = 'Agent';
    url = `${environment.blenderUrl}/agentHub`;

    protected override onStart() {
        if (this.#heartbeatSub) {
            return;
        }

        this.#heartbeatSub = interval(environment.heartbeatInterval * 1000)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(() => {
                this.heartbeat();
            });
    }

    protected override onStop() {
        this.#heartbeatSub?.unsubscribe();
        this.#heartbeatSub = undefined;
    }

    protected override registerHandlers() {
        this.hubConnection.on('GetState', (agentState: IAgentClientState) => {
            this.messageReceived();

            this.logger.verbose('Agent Hub Service (GetState) ->');
            this.logger.table(agentState);

            this.#agentStateSubject.next(agentState);
        });

        this.hubConnection.on(
            'StateTransitionRequested',
            (agentStateTransitionRequested: IAgentStateTransitionRequested) => {
                this.messageReceived();

                this.logger.verbose(
                    'Agent Hub Service (StateTransitionRequested) ->',
                    agentStateTransitionRequested,
                );
                this.logger.table(agentStateTransitionRequested);

                this.#agentTransitionSubject.next(
                    agentStateTransitionRequested,
                );
            },
        );

        this.hubConnection.on(
            'OnWorkItemClosed',
            (workItemClosedEvent: IWorkItemClosedEvent) => {
                this.messageReceived();

                this.logger.verbose(
                    'Agent Hub Service (OnWorkItemClosed) ->',
                    workItemClosedEvent,
                );
                this.logger.table(workItemClosedEvent);

                this.#workItemClosedSubject.next(workItemClosedEvent);
            },
        );
    }

    protected override unregisterHandlers() {
        this.hubConnection.off('GetState');
        this.hubConnection.off('StateTransitionRequested');
        this.hubConnection.off('OnWorkItemClosed');
    }

    heartbeat() {
        if (this.hubConnection.state === HubConnectionState.Connected) {
            this.logger.verbose('Agent Hub Service -> Heartbeat sent');
            this.hubConnection.send('HeartBeat');
        }
    }
}
